import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, Lock, Mail, ShieldCheck, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, auth, ensureSeed } from "@/lib/api";

const BG = "https://static.prod-images.emergentagent.com/jobs/a02ee5ba-9292-4d7c-afd3-1befd3a05625/images/8a4e9126b10eddef6be451893d00821e08abbbc17ff286941b9ee86dd3c4b143.png";

export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("physician");
  const [totp, setTotp] = useState("");
  const [step, setStep] = useState("credentials"); // credentials | totp
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Geo-recovery inline flow — surfaced when backend returns structured 403 GEO_ANOMALY_BLOCKED.
  const [geoBlock, setGeoBlock] = useState(null);   // {message, known_locations, recovery_options, current_country}
  const [recoveryCode, setRecoveryCode] = useState("");

  const submitGeoRecovery = async () => {
    if (!recoveryCode.trim()) {
      toast.error("Enter a backup recovery code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/geo-recovery", {
        email, password, recovery_code: recoveryCode.trim(),
        totp_code: totp || null,
      });
      localStorage.setItem("medlogix_token", data.token);
      localStorage.setItem("medlogix_user", JSON.stringify(data.user));
      localStorage.setItem("medlogix_session_id", data.session_id);
      toast.success(`Location added. Welcome, ${data.user.name}`);
      navigate(data.user.baa_acknowledged ? "/dashboard" : "/baa");
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Recovery failed");
      toast.error(msg);
      if (err.response?.status === 400 && msg.toLowerCase().includes("totp")) {
        // Re-open TOTP field so user can enter it
        setStep("totp");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { ensureSeed(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        const { data } = await api.post("/auth/register", { email, password, name, role });
        toast.success("Account created. Sign in to set up 2FA.");
        setMode("login");
        setLoading(false);
        return;
      }
      const { data } = await api.post("/auth/login", { email, password, totp_code: totp || null });
      if (data.must_setup_2fa) {
        // First login — got setup_token, navigate to 2FA setup
        localStorage.setItem("medlogix_token", data.setup_token);
        localStorage.setItem("medlogix_user", JSON.stringify({ id: data.user_id, email, baa_acknowledged: false, twofa_pending_setup: true }));
        toast.info("2FA setup required");
        navigate("/2fa-setup");
        return;
      }
      if (data.requires_2fa) {
        setStep("totp");
        toast.info("Enter your 6-digit authenticator code");
        setLoading(false);
        return;
      }
      // Full login success
      localStorage.setItem("medlogix_token", data.token);
      localStorage.setItem("medlogix_user", JSON.stringify(data.user));
      localStorage.setItem("medlogix_session_id", data.session_id);
      toast.success(`Welcome, ${data.user.name}`);
      navigate(data.user.baa_acknowledged ? "/dashboard" : "/baa");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured geo-anomaly 403: switch UI into inline recovery card.
      if (err.response?.status === 403 && detail && typeof detail === "object" && detail.code === "GEO_ANOMALY_BLOCKED") {
        setGeoBlock(detail);
        toast.error("Sign-in blocked from a new location. See recovery options below.");
        return;
      }
      const msg = typeof detail === "string" ? detail : (detail?.message || "Authentication failed");
      toast.error(msg);
      if (err.response?.status === 423) {
        // locked — offer recovery
        setTimeout(() => navigate("/recover"), 1500);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="login-bg flex" data-testid="login-page">
      <div className="hidden lg:flex w-1/2 relative items-center justify-center p-12 overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative z-10 max-w-md text-white">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-xl font-semibold">ZidaMD</div>
              <div className="text-[11px] uppercase tracking-[0.18em] opacity-80">by CamelionAI</div>
            </div>
          </div>
          <h1 className="text-4xl xl:text-5xl font-semibold leading-tight tracking-tight">
            Built to adapt.<br />Designed to perform.
          </h1>
          <p className="mt-6 text-white/90 max-w-md leading-relaxed text-base">
            HIPAA-grade AI for clinical decision support, documentation, and reimbursement — built for physicians and their clinical teams.
          </p>
          {/* Trust bar */}
          <div className="mt-6 flex flex-wrap gap-x-3 gap-y-1.5 text-[11px] text-white/80 max-w-md" data-testid="trust-bar">
            <span>Patent Pending USPTO #64/078,696</span>
            <span className="opacity-50">|</span>
            <span>HIPAA Compliant</span>
            <span className="opacity-50">|</span>
            <span>Hiscox Insured</span>
            <span className="opacity-50">|</span>
            <span>Anthropic BAA</span>
            <span className="opacity-50">|</span>
            <span>FDA CDS Aligned</span>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 text-sm">
            {["TOTP 2FA", "WAF + IP Rep", "Geo Anomaly", "Recovery Flow"].map((t) => (
              <div key={t} className="flex items-center gap-2 text-white/90">
                <ShieldCheck className="w-4 h-4 text-[#9B59B6]" />
                {t}
              </div>
            ))}
          </div>
          {/* About / Founder trust block */}
          <div className="mt-10 p-4 rounded-lg bg-white/8 border border-white/15 backdrop-blur text-[12px] text-white/85 leading-relaxed max-w-md" data-testid="about-founder">
            Built by <span className="text-white font-medium">Dr. Ronik Seecharan, DMD</span> — Founder &amp; CEO, CamelionAI LLC, Boca Raton FL.
            <div className="text-white/65 mt-1">Built by a clinician, for clinicians.</div>
          </div>
          <div className="mt-8 text-[11px] text-white/60 leading-relaxed">
            ZidaMD by CamelionAI LLC • Boca Raton, FL<br />
            Patent Pending USPTO #64/078,696 | #64/078,734 | #64/062,147
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 bg-brand-bg">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6 flex items-center gap-2 text-brand-primary">
            <Stethoscope className="w-6 h-6" />
            <div className="font-semibold">ZidaMD</div>
          </div>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-text mb-2">
            {geoBlock ? "New sign-in location detected"
              : step === "totp" ? "Two-factor verification"
              : (mode === "login" ? "Sign in" : "Create account")}
          </h2>
          <p className="text-sm text-brand-muted mb-6 sm:mb-8">
            {geoBlock ? "Verify it's really you with a backup recovery code."
              : step === "totp" ? "Open your authenticator app and enter the 6-digit code."
              : mode === "login" ? "2FA recommended. WAF + IP reputation enforced." : "Register as Physician, NP/PA, MA/Scribe, Front Desk, or Admin."}
          </p>

          {geoBlock && (
            <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="geo-recovery-card">
              <div className="flex items-start gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-900 leading-relaxed">
                  {geoBlock.message}
                  {geoBlock.known_locations?.length > 0 && (
                    <div className="mt-1">
                      <b>Locations we know:</b> {geoBlock.known_locations.join(", ")}
                    </div>
                  )}
                </div>
              </div>
              <label className="block text-xs font-medium text-amber-900 mb-1.5">
                Backup recovery code
              </label>
              <input
                type="text"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value)}
                placeholder="XXXX-XXXX"
                className="w-full px-3 py-2 bg-white border border-amber-300 rounded-md text-sm font-mono mb-2"
                data-testid="geo-recovery-code-input"
              />
              {step === "totp" && (
                <>
                  <label className="block text-xs font-medium text-amber-900 mb-1.5">
                    Authenticator code
                  </label>
                  <input
                    type="text" inputMode="numeric" maxLength={6}
                    value={totp} onChange={(e) => setTotp(e.target.value)}
                    placeholder="123456"
                    className="w-full px-3 py-2 bg-white border border-amber-300 rounded-md text-sm font-mono mb-2"
                    data-testid="geo-recovery-totp-input"
                  />
                </>
              )}
              <div className="flex gap-2 mt-3">
                <button
                  type="button" onClick={submitGeoRecovery} disabled={loading}
                  className="flex-1 px-3 py-2 rounded-md bg-brand-primary text-white text-sm font-medium disabled:opacity-60"
                  data-testid="geo-recovery-submit"
                >
                  {loading ? "Verifying…" : "Unblock this location"}
                </button>
                <button
                  type="button" onClick={() => { setGeoBlock(null); setRecoveryCode(""); }}
                  className="px-3 py-2 rounded-md border border-amber-300 text-amber-900 text-sm hover:bg-amber-100"
                  data-testid="geo-recovery-cancel"
                >
                  Cancel
                </button>
              </div>
              <p className="text-[11px] text-amber-800 mt-3 leading-relaxed">
                No recovery codes on hand? Email{" "}
                <a href={`mailto:${geoBlock.recovery_options?.find(o=>o.kind==="admin_reset")?.contact || "support@zidamd.com"}`}
                   className="underline font-medium">
                  {geoBlock.recovery_options?.find(o=>o.kind==="admin_reset")?.contact || "support@zidamd.com"}
                </a>{" "}
                with your email and the country shown above and support will add the location manually.
              </p>
            </div>
          )}

          <form onSubmit={submit} className={`space-y-4 ${geoBlock ? "opacity-50 pointer-events-none" : ""}`} data-testid="login-form">
            {step === "credentials" && mode === "register" && (
              <>
                <div>
                  <label className="block text-xs font-medium text-brand-muted mb-1.5">Full Name</label>
                  <input value={name} onChange={(e)=>setName(e.target.value)} required
                    placeholder="Dr. Jane Smith"
                    className="w-full px-3 py-3 bg-white border border-brand rounded-md text-base"
                    data-testid="register-name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-brand-muted mb-1.5">Role</label>
                  <select value={role} onChange={(e)=>setRole(e.target.value)}
                    className="w-full px-3 py-3 bg-white border border-brand rounded-md text-base"
                    data-testid="register-role">
                    <option value="physician">Physician (full clinical)</option>
                    <option value="np_pa">NP / PA (requires co-sign)</option>
                    <option value="ma_scribe">Medical Assistant / Scribe</option>
                    <option value="front_desk">Front Desk / Admin</option>
                    <option value="admin">Super Admin</option>
                  </select>
                </div>
              </>
            )}
            {step === "credentials" && (
              <>
                <div>
                  <label className="block text-xs font-medium text-brand-muted mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                    <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                      autoComplete="email" inputMode="email"
                      className="w-full pl-9 pr-3 py-3 bg-white border border-brand rounded-md text-base"
                      data-testid="login-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-brand-muted mb-1.5">Password</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                    <input type={showPassword ? "text" : "password"} required value={password} onChange={(e)=>setPassword(e.target.value)}
                      autoComplete="current-password"
                      className="w-full pl-9 pr-10 py-3 bg-white border border-brand rounded-md text-base"
                      data-testid="login-password" />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-brand-muted hover:text-brand-text"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                      data-testid="login-password-toggle">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </>
            )}
            {step === "totp" && (
              <div>
                <label className="block text-xs font-medium text-brand-muted mb-1.5">6-digit code</label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                  <input type="text" inputMode="numeric" maxLength={6} required autoFocus
                    autoComplete="one-time-code"
                    value={totp} onChange={(e)=>setTotp(e.target.value.replace(/\D/g, ""))}
                    className="w-full pl-9 pr-3 py-3 bg-white border border-brand rounded-md text-lg font-mono tracking-widest text-center"
                    data-testid="login-totp" />
                </div>
                <a href="/reset-2fa" className="mt-2 block text-xs text-brand-muted hover:text-brand-primary text-center"
                   data-testid="login-reset-2fa-link">
                  Codes keep being rejected? Reset your authenticator
                </a>
              </div>
            )}
            <button type="submit" disabled={loading}
              className="w-full bg-brand-primary text-white py-3 rounded-md text-sm font-medium hover:bg-[#35096B] transition-colors disabled:opacity-60"
              style={{ minHeight: 48 }}
              data-testid="login-submit">
              {loading ? "Please wait…" : (step === "totp" ? "Verify" : mode === "login" ? "Sign in" : "Request Beta Access")}
            </button>

            {step === "credentials" && mode === "login" && (
              <button type="button" onClick={()=>navigate("/recover")}
                className="w-full text-xs text-brand-muted hover:text-brand-primary py-2"
                style={{ minHeight: 44 }}
                data-testid="recover-link">
                Lost access? Recover account
              </button>
            )}
          </form>

        </div>
      </div>
    </div>
  );
}
